import logo from './logo.svg';
import './App.css';
import React, { useEffect, useRef } from 'react';
import * as BABYLON from '@babylonjs/core';

import { createScene as createScene_main } from './scenes/main-scene';
import DrawerList from './components/DrawerList'
import Drawer from '@mui/material/Drawer';
import WelcomeDialog from './components/WelcomeDialog';
import CompatWarningDialog from './components/CompatWarningDialog';
import { StarSystemSummaryPanel } from './components/StarSystemSummaryPanel';
import { deregisterEventHandler, registerEventHandler } from './util/Events';
import { loadExoplanetsData, mapDatasetPlanetTypeToEnum } from './dataprocessing/data-exoplanets';
import { Legend } from './components/Legend';

export const state = {
  showStars: false,
  dbgStr: "",
  distanceUnitsChosen: undefined,
}

export const data_vis_parameters = {
  filters: {}
}

async function initAsync(canvasRef, onTabKeyLambda, setWelcomeDialogOpen, setShowCompatWarning) {
  if (!canvasRef.current) return;

  const supported = await BABYLON.WebGPUEngine.IsSupportedAsync;
  if (!supported) {
    setShowCompatWarning(true);
    return;
  }

  const engine = new BABYLON.WebGPUEngine(canvasRef.current);
  await engine.initAsync();
  state.scene = createScene_main(engine, canvasRef.current, onTabKeyLambda);

  setWelcomeDialogOpen(true)

  let divFps = document.getElementById("fps");
  engine.runRenderLoop(function () {
    state.scene.render();
    divFps.innerHTML = engine.getFps().toFixed() + " fps<br>" + state.dbgStr;
  });

  window.addEventListener("resize", function () {
    engine.resize();
  });
}

let exoplanetsData = {}
function App() {
  const canvasRef = useRef(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [legendOpen, setLegendOpen] = React.useState(false);
  const [welcomeDialogOpen, setWelcomeDialogOpen] = React.useState(false);
  const [showCompatWarning, setShowCompatWarning] = React.useState(false);
  const [selectedPlanetTypes, setSelectedPlanetTypes] = React.useState([]);
  const [selectedStarName, setSelectedStarName] = React.useState('');
  const [selectionLabelAlpha, setSelectionLabelAlpha] = React.useState(0);

  function handleDrawerClose() {
    setDrawerOpen(false);
  }

  function onTabKeyLambda() {
    if (showCompatWarning) return;
    setDrawerOpen(!drawerOpen);
    setWelcomeDialogOpen(false);
  }

  function onStarSelectionChanged({ starName, positionSS }) {
    setSelectedStarName(starName)

    const element = document.getElementById('star-system-summary-panel')
    element.style.left = positionSS.x + 'px'
    element.style.top = positionSS.y + 'px'

    const planetTypes = []
    if (exoplanetsData[starName]) {
      //Sort in order of ascending distance from star
      const planets_sorted = Object.values(exoplanetsData[starName]).sort((a, b) => (a.pl_orbsmax || 0) - (b.pl_orbsmax || 0))
      planets_sorted.forEach(data => {
        planetTypes.push(mapDatasetPlanetTypeToEnum(data.planet_type))
      });
    }
    setSelectedPlanetTypes(planetTypes)
  }

  //TODO: Determine if this will cause memory leaks or similar problems (no cleanup, run on each reload)
  useEffect(() => {
    //Main app init
    initAsync(canvasRef, onTabKeyLambda, setWelcomeDialogOpen, setShowCompatWarning);

    //Keydown handler
    const handleKeyDown = (event) => {
      if (event.key === 'Tab') {
        state.onTabKeyLambda();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    //Event handlers
    registerEventHandler('starSelectionChanged', 'app', onStarSelectionChanged)
    registerEventHandler('openLegend', 'app', openLegend)
    registerEventHandler('updateStarSystemLabelAlpha', 'app', setSelectionLabelAlpha)

    //Deconstructor
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      deregisterEventHandler('starSelectionChanged', 'app')
      deregisterEventHandler('updateStarSystemLabelAlpha', 'app')
    };

  }, []);

  useEffect(() => {
    state.onTabKeyLambda = onTabKeyLambda; //The lambda needs to be current to the latest re-render
  });

  useEffect(() => {
    loadExoplanetsData().then(data => exoplanetsData = data)
  }, [])

  function openLegend() {
    setDrawerOpen(false)
    setLegendOpen(true)
  }

  return (
    <div className="App">
      <header className="App-header">
        <img src='https://images.pexels.com/photos/53594/blue-clouds-day-fluffy-53594.jpeg?cs=srgb&dl=pexels-pixabay-53594.jpg&fm=jpg' style={{ position: 'absolute', width: '100%', height: '100%', zIndex: -1 }}></img>
        <canvas id="renderCanvas" ref={canvasRef}></canvas>

        <div id="fps"></div>
        <div id="topleft-hint">Press [TAB] to customize</div>
      </header>
      <Drawer open={drawerOpen} variant="persistent" onClose={handleDrawerClose}>
        {<DrawerList handleDrawerClose={handleDrawerClose} />}
      </Drawer>
      {showCompatWarning
        ? <CompatWarningDialog></CompatWarningDialog>
        : <WelcomeDialog open={welcomeDialogOpen} onClose={() => { setWelcomeDialogOpen(false); }} />
      }
      <Legend open={legendOpen} handleClose={() => { setLegendOpen(false) }} />
      <StarSystemSummaryPanel size={50} planets={selectedPlanetTypes} hostname={selectedStarName} alpha={selectionLabelAlpha} />
    </div>
  );
}

export default App;
